import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import type { ICampaign } from '../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { commissionEstimateSummary, formatCommissionPoolDollars } from '../../utils/commissionCampaign';
import { ROUTES } from '../../types/global/routes.types';
import { replaceRouteParam } from '../../utils/routesProcessor';

interface Props {
    campaign: ICampaign;
    accent?: string;
}

export function CampaignCommissionDetailBlock({ campaign, accent = '#ffc965' }: Props) {
    if (campaign.campaign_type !== 'commission') return null;

    const estimate = campaign.commission_estimate;
    const rate = campaign.commission?.rate ?? estimate?.commission_rate;
    const readme = campaign.influencer_readme?.trim();
    const estimateLine = commissionEstimateSummary(estimate);
    const contest = campaign.commission_contest;
    const external = campaign.commission_external || campaign.commission?.external;
    const poolCents =
        estimate?.pool_cents ??
        campaign.commission?.display_pool_cents ??
        (typeof campaign.possible_earning_cents === 'number' ? campaign.possible_earning_cents : null);
    const basePayCents = Math.round(Number((campaign as any).base_payment_per_creator_cents) || 0);
    const basePayUsd = basePayCents > 0 ? (basePayCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }) : null;

    return (
        <Box
            sx={{
                p: 2.5,
                borderRadius: 2,
                bgcolor: 'rgba(255,201,101,0.06)',
                border: '1px solid rgba(255,201,101,0.18)',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PercentOutlinedIcon sx={{ fontSize: 16, color: accent }} />
                <Typography
                    sx={{
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        color: accent,
                    }}>
                    Commission campaign
                </Typography>
            </Box>

            {readme ? (
                <Box>
                    <Typography
                        sx={{
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: 'rgba(255,255,255,0.45)',
                            mb: 1,
                        }}>
                        What you&apos;ll do
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 15,
                            color: 'rgba(255,255,255,0.85)',
                            lineHeight: 1.65,
                            whiteSpace: 'pre-wrap',
                        }}>
                        {readme}
                    </Typography>
                </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {rate != null ? <Chip label={`${rate}% commission`} accent={accent} /> : null}
                {basePayUsd != null ? <Chip label={`+ ${basePayUsd} base pay`} accent={accent} /> : null}
                {estimateLine ? <Chip label={estimateLine} accent={accent} /> : null}
                {poolCents != null ? (
                    <Chip label={`Pool ${formatCommissionPoolDollars(poolCents)}`} accent={accent} />
                ) : null}
                <Chip label="Paid after campaign ends" accent={accent} />
            </Box>

            {basePayUsd != null ? (
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(255,201,101,0.08)',
                        border: '1px solid rgba(255,201,101,0.2)',
                    }}>
                    <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
                        <Typography component="span" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: accent, fontSize: 13 }}>
                            {basePayUsd} base pay
                        </Typography>
                        {' '}is paid to you upon content approval, on top of your commission earnings.
                    </Typography>
                </Box>
            ) : null}

            {contest?._id || contest?.title ? (
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <EmojiEventsOutlinedIcon sx={{ fontSize: 16, color: accent }} />
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                            Linked contest
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                        {contest.title || 'Contest'}
                    </Typography>
                    {contest.slug || contest._id ? (
                        <Link
                            href={replaceRouteParam(
                                ROUTES.ESVIEW_CONTEST,
                                'contestId',
                                contest.slug || contest._id || '',
                            )}
                            underline="hover"
                            sx={{ fontSize: 13, color: accent }}>
                            View contest
                        </Link>
                    ) : null}
                </Box>
            ) : null}

            {external?.product_name ? (
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 1.5,
                        bgcolor: 'rgba(0,0,0,0.25)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <StorefrontOutlinedIcon sx={{ fontSize: 16, color: accent }} />
                        <Typography sx={{ fontSize: 12, fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                            Product
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', mb: 0.5 }}>
                        {external.product_name}
                        {external.store_name ? ` · ${external.store_name}` : ''}
                    </Typography>
                    {external.product_description ? (
                        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: 1, lineHeight: 1.5 }}>
                            {external.product_description}
                        </Typography>
                    ) : null}
                    {external.product_url ? (
                        <Link
                            href={external.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            sx={{ fontSize: 13, color: accent }}>
                            View product
                        </Link>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}

function Chip({ label, accent }: { label: string; accent: string }) {
    return (
        <Box
            sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                border: `1px solid ${accent}55`,
                bgcolor: `${accent}14`,
                fontSize: 11,
                fontFamily: FONT_ENUM.MAIN_BOLD,
                color: accent,
            }}>
            {label}
        </Box>
    );
}
